import mitt from '@livechat/mitt';
import { buildQueryString } from '@livechat/url-utils';
import createBackoff from '@livechat/backoff';

// based on WebSocket's readyState - https://www.w3.org/TR/websockets/#the-websocket-interface
var CONNECTING = 0;
var OPEN = 1;
var CLOSED = 3;

var readyStates = /*#__PURE__*/Object.freeze({
	__proto__: null,
	CONNECTING: CONNECTING,
	OPEN: OPEN,
	CLOSED: CLOSED
});

// I've tried upgrading but it had some other problems and I couldn't debug the problem quickly

var createWebSocketManager = function createWebSocketManager(url, _temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      _ref$query = _ref.query,
      query = _ref$query === void 0 ? {} : _ref$query;

  var queryString = buildQueryString(query);
  var finalUrl = queryString ? url + "?" + queryString : url;
  var emitter = mitt();
  var backoff = createBackoff({
    min: 1000,
    max: 5000,
    jitter: 0.5
  });
  var state = CLOSED;
  var reconnectTimer;
  var socket = null;

  var openListener = function openListener() {
    state = OPEN;
    backoff.reset();
    emitter.emit('connect');
  };

  var closeListener = function closeListener() {
    close();
    reconnect();
    emitter.emit('disconnect');
  };

  var messageListener = function messageListener(_ref2) {
    var data = _ref2.data;
    emitter.emit('message', data);
  };

  var addEventListeners = function addEventListeners(instance) {
    instance.addEventListener('open', openListener);
    instance.addEventListener('close', closeListener);
    instance.addEventListener('message', messageListener);
  };

  var removeEventListeners = function removeEventListeners(instance) {
    instance.removeEventListener('open', openListener);
    instance.removeEventListener('close', closeListener);
    instance.removeEventListener('message', messageListener);
  };

  var close = function close() {
    clearTimeout(reconnectTimer);
    state = CLOSED;

    if (!socket) {
      return;
    }

    removeEventListeners(socket);
    socket.close();
    socket = null;
  };

  var connect = function connect() {
    state = CONNECTING;
    socket = new WebSocket(finalUrl);
    addEventListeners(socket);
  };

  var reconnect = function reconnect(delay) {
    if (delay === void 0) {
      delay = backoff.duration();
    }

    close();

    if (delay === 0) {
      connect();
      return;
    }

    reconnectTimer = setTimeout(connect, delay);
  };

  return {
    connect: function (_connect) {
      function connect() {
        return _connect.apply(this, arguments);
      }

      connect.toString = function () {
        return _connect.toString();
      };

      return connect;
    }(function () {
      if (state !== CLOSED) {
        throw new Error('Socket is already open or connecting.');
      }

      clearTimeout(reconnectTimer);
      connect();
    }),
    destroy: function destroy() {
      emitter.off();
      close();
    },
    disconnect: close,
    reconnect: reconnect,
    emit: function emit(data) {
      if (state !== OPEN) {
        throw new Error('Socket is not connected.');
      } // if we are connected we can be sure that socket is defined


      socket.send(data);
    },
    getReadyState: function getReadyState() {
      return state;
    },
    on: emitter.on,
    off: emitter.off
  };
};

export default createWebSocketManager;
export { readyStates };
